import { useCallback, useEffect, useRef, useState } from "react"
import type { ImperativePanelHandle } from "react-resizable-panels"
import {
  COLLAPSED_CHAT_STRIP_MAX_PCT,
  DEFAULT_RESIZABLE_PANEL_TRIPLE,
  SHELL_AGENT_FLOOR_PX,
  SHELL_LEFT_FLOOR_PX,
  expandedChatPanelPercent,
  expandedLeftPanelPercent,
  nextSidebarPinToggle,
  rebasePanelPercentForGroupWidthChange,
  resolveShellColumnWidth,
  shellFrameCanDock,
  shellPanelGroupWidthPx,
} from "./resizable-layout.utils"
import type { useResizableLayoutState } from "./resizable-layout.hooks"
import { SHELL_ICON_RAIL_PX } from "@/lib/shell-chrome"
import { dispatchAgentPanelVisibility } from "@/lib/agent-panel-visibility"

export type ShellColumnPanelsInput = Pick<
  ReturnType<typeof useResizableLayoutState>,
  | "showSidebar"
  | "setShowSidebar"
  | "sidebarCollapsed"
  | "setSidebarCollapsed"
  | "sidebarAutoHide"
  | "setSidebarAutoHide"
  | "showAgent"
  | "setShowAgent"
  | "agentCollapsed"
  | "setAgentCollapsed"
  | "panelSizes"
  | "setPanelSizes"
  | "expandedLeftSize"
  | "setExpandedLeftSize"
  | "expandedRightSize"
  | "setExpandedRightSize"
  | "isFullscreen"
>

export function useShellColumnPanels(input: ShellColumnPanelsInput) {
  const {
    showSidebar,
    setShowSidebar,
    sidebarCollapsed,
    setSidebarCollapsed,
    sidebarAutoHide,
    setSidebarAutoHide,
    showAgent,
    setShowAgent,
    agentCollapsed,
    setAgentCollapsed,
    panelSizes,
    setPanelSizes,
    expandedLeftSize,
    setExpandedLeftSize,
    expandedRightSize,
    setExpandedRightSize,
    isFullscreen,
  } = input

  const chatPanelRef = useRef<ImperativePanelHandle | null>(null)
  const sidebarPanelRef = useRef<ImperativePanelHandle | null>(null)
  /** Shell row under the app header - used to measure PanelGroup width vs icon rails. */
  const shellRowRef = useRef<HTMLDivElement | null>(null)
  const prevAgentCollapsedRef = useRef(agentCollapsed)
  const prevSidebarCollapsedRef = useRef(sidebarCollapsed)
  const prevShowAgentRef = useRef(showAgent)
  /** Skip onLayout persistence while imperative resize restores width after strip expand. */
  const restoringChatWidthRef = useRef(false)
  const restoringSidebarWidthRef = useRef(false)
  /** Sync snapshot of last expanded chat %; survives onLayout strip noise during collapse transition. */
  const lastExpandedChatWidthRef = useRef(
    expandedRightSize > COLLAPSED_CHAT_STRIP_MAX_PCT
      ? expandedRightSize
      : (panelSizes[2] > COLLAPSED_CHAT_STRIP_MAX_PCT ? panelSizes[2] : DEFAULT_RESIZABLE_PANEL_TRIPLE[2]),
  )
  /** Sync snapshot of last expanded browse %; survives onLayout strip noise during collapse transition. */
  const lastExpandedLeftWidthRef = useRef(
    expandedLeftSize > COLLAPSED_CHAT_STRIP_MAX_PCT
      ? expandedLeftSize
      : (panelSizes[0] > COLLAPSED_CHAT_STRIP_MAX_PCT ? panelSizes[0] : DEFAULT_RESIZABLE_PANEL_TRIPLE[0]),
  )
  /** Ignore panel-group onLayout while a floor snap is unmounting that column. */
  const collapsingLeftRef = useRef(false)
  const collapsingAgentRef = useRef(false)
  /** True while the pointer is down on a shell resize handle. */
  const shellDragRef = useRef(false)
  /** True after a floor snap until that same pointer is released. */
  const snapGestureRef = useRef(false)
  const gestureEdgesRef = useRef(new Set<'left' | 'agent'>())
  const gestureStopRef = useRef<(() => void) | null>(null)
  const sidebarCollapsedRef = useRef(sidebarCollapsed)
  const agentCollapsedRef = useRef(agentCollapsed)
  const sidebarAutoHideRef = useRef(sidebarAutoHide)
  const measureShellRef = useRef<(() => { roomForLeft: boolean; roomForAgent: boolean })>(() => ({
    roomForLeft: false,
    roomForAgent: false,
  }))
  const gestureMoveRef = useRef<(ev: PointerEvent) => void>(() => {})
  const gestureCommitRef = useRef<(edges: Set<'left' | 'agent'>) => void>(() => {})
  sidebarCollapsedRef.current = sidebarCollapsed
  agentCollapsedRef.current = agentCollapsed
  sidebarAutoHideRef.current = sidebarAutoHide
  const [shellRowWidth, setShellRowWidth] = useState(0)
  const [flyoutOpen, setFlyoutOpen] = useState(false)
  /** In-memory pin. Auto-hide on starts unpinned (collapsed). Not persisted. */
  const [sidebarPinned, setSidebarPinned] = useState(false)
  /** True after auto-hide has shown the rail. A later external dock counts as a pin. */
  const autoHideSawRailRef = useRef(false)

  const sidebarInGroup = Boolean(showSidebar && !sidebarCollapsed && !isFullscreen)
  const agentInGroup = Boolean(showAgent && !agentCollapsed && !isFullscreen)

  /** Measure the shell row and whether each column can dock at its floor. */
  const measureShell = useCallback(() => {
    const row = shellRowRef.current?.clientWidth ?? shellRowWidth
    const sidebarRailPx = showSidebar && sidebarCollapsed && !isFullscreen ? SHELL_ICON_RAIL_PX : 0
    const agentRailPx = showAgent && agentCollapsed && !isFullscreen ? SHELL_ICON_RAIL_PX : 0
    const group = shellPanelGroupWidthPx(row, { sidebarRailPx, agentRailPx })
    const liveLeft = sidebarPanelRef.current?.getSize()
    const liveChat = chatPanelRef.current?.getSize()
    const leftPct = sidebarInGroup
      ? (typeof liveLeft === "number" ? liveLeft : expandedLeftPanelPercent(panelSizes, expandedLeftSize))
      : 0
    const chatPct = agentInGroup
      ? (typeof liveChat === "number" ? liveChat : expandedChatPanelPercent(panelSizes, expandedRightSize))
      : 0
    const leftPx = sidebarInGroup
      ? (leftPct / 100) * group
      : (showSidebar && !isFullscreen ? SHELL_ICON_RAIL_PX : 0)
    const agentPx = agentInGroup
      ? (chatPct / 100) * group
      : (showAgent && !isFullscreen ? SHELL_ICON_RAIL_PX : 0)
    const measured = row > 0
    return {
      row,
      group,
      roomForLeft: !measured || shellFrameCanDock(row, agentPx, SHELL_LEFT_FLOOR_PX),
      roomForAgent: !measured || shellFrameCanDock(row, leftPx, SHELL_AGENT_FLOOR_PX),
    }
  }, [
    shellRowWidth,
    showSidebar,
    sidebarCollapsed,
    isFullscreen,
    showAgent,
    agentCollapsed,
    sidebarInGroup,
    agentInGroup,
    panelSizes,
    expandedLeftSize,
    expandedRightSize,
  ])
  measureShellRef.current = measureShell

  const resolveRestoreChatTarget = useCallback(() => {
    const fromRef = lastExpandedChatWidthRef.current
    if (fromRef > COLLAPSED_CHAT_STRIP_MAX_PCT) return fromRef
    return expandedChatPanelPercent(panelSizes, expandedRightSize)
  }, [panelSizes, expandedRightSize])

  const resolveRestoreSidebarTarget = useCallback(() => {
    const fromRef = lastExpandedLeftWidthRef.current
    if (fromRef > COLLAPSED_CHAT_STRIP_MAX_PCT) return fromRef
    return expandedLeftPanelPercent(panelSizes, expandedLeftSize)
  }, [panelSizes, expandedLeftSize])

  /** Snapshot live % before any path collapses browse panel (strip). */
  const persistSidebarExpandedWidthIfExpanded = useCallback(() => {
    if (sidebarCollapsed || !showSidebar || isFullscreen) return
    const fromPanel = sidebarPanelRef.current?.getSize()
    const width =
      typeof fromPanel === "number" && fromPanel > COLLAPSED_CHAT_STRIP_MAX_PCT
        ? fromPanel
        : expandedLeftPanelPercent(panelSizes, expandedLeftSize)
    lastExpandedLeftWidthRef.current = width
    setExpandedLeftSize(width)
    setPanelSizes((prev) => {
      const right = prev[2] ?? expandedRightSize ?? DEFAULT_RESIZABLE_PANEL_TRIPLE[2]
      return [width, 100 - width - right, right]
    })
  }, [
    sidebarCollapsed,
    showSidebar,
    isFullscreen,
    panelSizes,
    expandedLeftSize,
    expandedRightSize,
    setExpandedLeftSize,
    setPanelSizes,
  ])

  /** Rail toggle, Cmd/Ctrl+B, and the mode-panel collapse item. */
  const toggleSidebarCollapsed = useCallback(() => {
    if (!showSidebar || isFullscreen) {
      const nextCollapsed = !sidebarCollapsed
      setSidebarCollapsed(nextCollapsed)
      if (nextCollapsed) setSidebarPinned(false)
      return
    }
    const decision = nextSidebarPinToggle({
      collapsed: sidebarCollapsed,
      autoHide: sidebarAutoHide,
      pinned: sidebarPinned,
      roomForLeft: measureShell().roomForLeft,
    })
    if (!sidebarCollapsed && decision.collapsed) {
      persistSidebarExpandedWidthIfExpanded()
    }
    setSidebarPinned(decision.pinned)
    setFlyoutOpen(decision.openFlyout)
    setSidebarCollapsed(decision.collapsed)
  }, [
    sidebarCollapsed,
    showSidebar,
    isFullscreen,
    sidebarAutoHide,
    sidebarPinned,
    persistSidebarExpandedWidthIfExpanded,
    setSidebarCollapsed,
    measureShell,
  ])

  /** Dock the sidebar from the collapsed rail when the frame can spare 248px. */
  const dockLeftFromRail = useCallback(() => {
    if (!measureShell().roomForLeft) return
    setFlyoutOpen(false)
    if (sidebarAutoHide) setSidebarPinned(true)
    setSidebarCollapsed(false)
  }, [measureShell, setSidebarCollapsed, sidebarAutoHide])

  /** Toggle auto-hide: on undocks the sidebar, off docks it when there is room. */
  const toggleSidebarAutoHide = useCallback(() => {
    const next = !sidebarAutoHide
    setSidebarAutoHide(next)
    setSidebarPinned(false)
    setFlyoutOpen(false)
    if (next) {
      if (!sidebarCollapsed && showSidebar && !isFullscreen) {
        persistSidebarExpandedWidthIfExpanded()
      }
      setSidebarCollapsed(true)
      return
    }
    if (measureShell().roomForLeft) {
      setShowSidebar(true)
      setSidebarCollapsed(false)
    }
  }, [
    sidebarAutoHide,
    setSidebarAutoHide,
    sidebarCollapsed,
    showSidebar,
    isFullscreen,
    persistSidebarExpandedWidthIfExpanded,
    setSidebarCollapsed,
    setShowSidebar,
    measureShell,
  ])

  /** Raise a stored panel percent so its pixel width is at least the floor. */
  const percentAtLeastFloor = useCallback((
    percent: number,
    floorPx: number,
    rails: { sidebarRailPx: number; agentRailPx: number },
  ) => {
    const row = shellRowRef.current?.clientWidth ?? 0
    const group = shellPanelGroupWidthPx(row, rails)
    if (group <= 0) return percent
    const px = (percent / 100) * group
    if (px >= floorPx) return percent
    return (floorPx / group) * 100
  }, [])

  const restoreSidebarPanelWidth = useCallback(() => {
    const stored = resolveRestoreSidebarTarget()
    const target = percentAtLeastFloor(stored, SHELL_LEFT_FLOOR_PX, {
      sidebarRailPx: 0,
      agentRailPx: showAgent && agentCollapsed && !isFullscreen ? SHELL_ICON_RAIL_PX : 0,
    })
    if (target !== stored) {
      lastExpandedLeftWidthRef.current = target
      setExpandedLeftSize(target)
    }
    restoringSidebarWidthRef.current = true
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        sidebarPanelRef.current?.resize(target)
        restoringSidebarWidthRef.current = false
      })
    })
  }, [
    resolveRestoreSidebarTarget,
    percentAtLeastFloor,
    showAgent,
    agentCollapsed,
    isFullscreen,
    setExpandedLeftSize,
  ])

  /** Snapshot live % before any path collapses chat (strip); keeps UI_EXPANDED_RIGHT_SIZE accurate. */
  const persistChatExpandedWidthIfExpanded = useCallback(() => {
    if (agentCollapsed || !showAgent || isFullscreen) return
    const fromPanel = chatPanelRef.current?.getSize()
    const width =
      typeof fromPanel === "number" && fromPanel > COLLAPSED_CHAT_STRIP_MAX_PCT
        ? fromPanel
        : expandedChatPanelPercent(panelSizes, expandedRightSize)
    lastExpandedChatWidthRef.current = width
    setExpandedRightSize(width)
    setPanelSizes((prev) => {
      const left = prev[0] ?? expandedLeftSize ?? 20
      return [left, 100 - left - width, width]
    })
  }, [
    agentCollapsed,
    showAgent,
    isFullscreen,
    panelSizes,
    expandedRightSize,
    expandedLeftSize,
    setExpandedRightSize,
    setPanelSizes,
  ])

  /**
   * Collapsed agent is a fixed w-12 sibling outside PanelGroup. When it joins
   * or leaves the group, the same left % maps to different pixels. Rebase left
   * % so browse column width stays stable across the toggle.
   * @returns next left % when rebased, otherwise null
   */
  const rebaseLeftPercentForAgentCollapsedChange = useCallback(
    (nextAgentCollapsed: boolean): number | null => {
      const sidebarInGroupNow = Boolean(showSidebar && !sidebarCollapsed && !isFullscreen)
      if (!sidebarInGroupNow) return null

      const outer = shellRowRef.current?.clientWidth
      if (!outer || outer <= 0) return null

      const sidebarRailPx =
        showSidebar && sidebarCollapsed && !isFullscreen ? SHELL_ICON_RAIL_PX : 0
      const agentRailNow =
        showAgent && agentCollapsed && !isFullscreen ? SHELL_ICON_RAIL_PX : 0
      const agentRailNext =
        showAgent && nextAgentCollapsed && !isFullscreen ? SHELL_ICON_RAIL_PX : 0

      const groupNow = shellPanelGroupWidthPx(outer, {
        sidebarRailPx,
        agentRailPx: agentRailNow,
      })
      const groupNext = shellPanelGroupWidthPx(outer, {
        sidebarRailPx,
        agentRailPx: agentRailNext,
      })
      if (groupNow === groupNext) return null

      const live = sidebarPanelRef.current?.getSize()
      const leftPct =
        typeof live === "number" && live > COLLAPSED_CHAT_STRIP_MAX_PCT
          ? live
          : expandedLeftPanelPercent(panelSizes, expandedLeftSize)
      const nextLeft = rebasePanelPercentForGroupWidthChange(leftPct, groupNow, groupNext)
      if (Math.abs(nextLeft - leftPct) < 0.01) return null

      lastExpandedLeftWidthRef.current = nextLeft
      setExpandedLeftSize(nextLeft)
      setPanelSizes((prev) => {
        const chat = expandedChatPanelPercent(prev, expandedRightSize)
        if (nextAgentCollapsed) {
          return [nextLeft, 100 - nextLeft, chat]
        }
        return [nextLeft, Math.max(0, 100 - nextLeft - chat), chat]
      })
      return nextLeft
    },
    [
      showSidebar,
      sidebarCollapsed,
      isFullscreen,
      showAgent,
      agentCollapsed,
      panelSizes,
      expandedLeftSize,
      expandedRightSize,
      setExpandedLeftSize,
      setPanelSizes,
    ],
  )

  const setAgentCollapsedPreservingLeftPx = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      const resolved = typeof next === "function" ? next(agentCollapsed) : next
      if (resolved === agentCollapsed) {
        setAgentCollapsed(resolved)
        return
      }
      const nextLeft = rebaseLeftPercentForAgentCollapsedChange(resolved)
      setAgentCollapsed(resolved)
      // Left Panel often survives the 2<->3 column remount; defaultSize will not re-apply.
      if (nextLeft != null) {
        restoringSidebarWidthRef.current = true
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            sidebarPanelRef.current?.resize(nextLeft)
            restoringSidebarWidthRef.current = false
          })
        })
      }
    },
    [agentCollapsed, rebaseLeftPercentForAgentCollapsedChange, setAgentCollapsed],
  )

  const expandAgentPanel = useCallback(() => {
    setShowAgent(true)
    setAgentCollapsedPreservingLeftPx(false)
  }, [setShowAgent, setAgentCollapsedPreservingLeftPx])

  const expandLeftSidebar = useCallback(() => {
    setShowSidebar(true)
    if (!measureShell().roomForLeft) return
    if (sidebarAutoHide) setSidebarPinned(true)
    setSidebarCollapsed(false)
  }, [setShowSidebar, setSidebarCollapsed, measureShell, sidebarAutoHide])

  /** Dock the agent from the collapsed strip when the frame can spare 320px. */
  const dockAgentFromRail = useCallback(() => {
    if (!measureShell().roomForAgent) return
    setAgentCollapsedPreservingLeftPx(false)
  }, [measureShell, setAgentCollapsedPreservingLeftPx])

  /** Cmd/Ctrl+J and the agent Bot: one code path. */
  const toggleAgentCollapsed = useCallback(() => {
    if (!showAgent || isFullscreen) {
      setAgentCollapsedPreservingLeftPx(!agentCollapsed)
      return
    }
    if (!agentCollapsed) {
      persistChatExpandedWidthIfExpanded()
      setAgentCollapsedPreservingLeftPx(true)
      return
    }
    if (!measureShell().roomForAgent) return
    setAgentCollapsedPreservingLeftPx(false)
  }, [
    agentCollapsed,
    showAgent,
    isFullscreen,
    persistChatExpandedWidthIfExpanded,
    setAgentCollapsedPreservingLeftPx,
    measureShell,
  ])

  const restoreChatPanelWidth = useCallback(() => {
    const stored = resolveRestoreChatTarget()
    const target = percentAtLeastFloor(stored, SHELL_AGENT_FLOOR_PX, {
      sidebarRailPx: showSidebar && sidebarCollapsed && !isFullscreen ? SHELL_ICON_RAIL_PX : 0,
      agentRailPx: 0,
    })
    if (target !== stored) {
      lastExpandedChatWidthRef.current = target
      setExpandedRightSize(target)
    }
    restoringChatWidthRef.current = true
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        chatPanelRef.current?.resize(target)
        restoringChatWidthRef.current = false
      })
    })
  }, [
    resolveRestoreChatTarget,
    percentAtLeastFloor,
    showSidebar,
    sidebarCollapsed,
    isFullscreen,
    setExpandedRightSize,
  ])

  // After chat strip expands, defaultSize does not re-apply; restore last % via imperative resize.
  useEffect(() => {
    const wasCollapsed = prevAgentCollapsedRef.current
    prevAgentCollapsedRef.current = agentCollapsed
    if (snapGestureRef.current) return
    if (!(wasCollapsed && !agentCollapsed && showAgent && !isFullscreen)) return
    restoreChatPanelWidth()
  }, [agentCollapsed, showAgent, isFullscreen, restoreChatPanelWidth])

  // Preview reading-margin "Add to chat" only when the agent panel is visible + expanded.
  useEffect(() => {
    dispatchAgentPanelVisibility(Boolean(showAgent && !agentCollapsed && !isFullscreen))
  }, [showAgent, agentCollapsed, isFullscreen])

  // Exiting fullscreen / re-showing agent remounts the panel; rAF resize aligns layout with persisted %.
  useEffect(() => {
    const prev = prevShowAgentRef.current
    prevShowAgentRef.current = showAgent
    const becameVisible =
      prev === false && showAgent === true && !isFullscreen && !agentCollapsed
    if (!becameVisible) return
    restoreChatPanelWidth()
  }, [showAgent, isFullscreen, agentCollapsed, restoreChatPanelWidth])

  // After browse strip expands, restore last % via imperative resize.
  useEffect(() => {
    const wasCollapsed = prevSidebarCollapsedRef.current
    prevSidebarCollapsedRef.current = sidebarCollapsed
    collapsingLeftRef.current = false
    if (snapGestureRef.current) return
    if (!(wasCollapsed && !sidebarCollapsed && showSidebar && !isFullscreen)) return
    restoreSidebarPanelWidth()
  }, [sidebarCollapsed, showSidebar, isFullscreen, restoreSidebarPanelWidth])

  useEffect(() => {
    collapsingAgentRef.current = false
  }, [agentCollapsed])

  useEffect(() => {
    if (!sidebarAutoHide || !sidebarCollapsed || isFullscreen || !showSidebar) {
      setFlyoutOpen(false)
    }
  }, [sidebarAutoHide, sidebarCollapsed, isFullscreen, showSidebar])

  /**
   * Gallery fitLayout: auto-hide keeps the list undocked unless it is pinned.
   * Load (or turning auto-hide on) starts on the rail. A later dock from outside
   * this hook, such as Reveal in sidebar, becomes a pin so it is not snapped shut.
   * Collapsing from outside, such as focus view, clears the pin.
   */
  useEffect(() => {
    if (!sidebarAutoHide || isFullscreen || !showSidebar) {
      if (!sidebarAutoHide) autoHideSawRailRef.current = false
      if (sidebarCollapsed && sidebarPinned) setSidebarPinned(false)
      return
    }
    if (sidebarCollapsed) {
      autoHideSawRailRef.current = true
      // Expand tweens while still collapsed. Keep the pin until the dock lands.
      if (sidebarPinned) setSidebarPinned(false)
      return
    }
    if (sidebarPinned) return
    if (!autoHideSawRailRef.current) {
      persistSidebarExpandedWidthIfExpanded()
      setFlyoutOpen(false)
      setSidebarCollapsed(true)
      return
    }
    setSidebarPinned(true)
  }, [
    sidebarAutoHide,
    sidebarCollapsed,
    sidebarPinned,
    showSidebar,
    isFullscreen,
    persistSidebarExpandedWidthIfExpanded,
    setSidebarCollapsed,
  ])

  useEffect(() => {
    const el = shellRowRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const publish = () => setShellRowWidth(el.clientWidth)
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const shellRoom = measureShell()
  const leftDockDisabledReason =
    sidebarCollapsed && showSidebar && !isFullscreen && !sidebarAutoHide && !shellRoom.roomForLeft
      ? "Frame is too narrow to dock"
      : undefined
  const agentDockDisabledReason =
    agentCollapsed && showAgent && !isFullscreen && !shellRoom.roomForAgent
      ? "Frame is too narrow to dock"
      : undefined

  /** Convert a panel percent to pixels for the current group width. */
  const columnPx = (pct: number | undefined, group: number) =>
    pct === undefined || group <= 0 ? 0 : (pct / 100) * group

  /** Percent for a pixel width once that column is docked (its rail is not in the group). */
  const dockedColumnPercent = (px: number, sidebarRail: boolean, agentRail: boolean) => {
    const row = shellRowRef.current?.clientWidth ?? 0
    const group = shellPanelGroupWidthPx(row, {
      sidebarRailPx: sidebarRail ? SHELL_ICON_RAIL_PX : 0,
      agentRailPx: agentRail ? SHELL_ICON_RAIL_PX : 0,
    })
    if (row <= 0 || group <= 0) return null
    return (px / group) * 100
  }

  /**
   * After a floor snap, the resize handle unmounts. Keep the same pointer
   * until release: under the floor stays the rail, crossing back docks at the
   * pointer (the floor at the crossing), and further movement resizes.
   */
  const applySnapEdge = (edge: 'left' | 'agent', pointerPx: number, roomForFloor: boolean) => {
    const floor = edge === 'left' ? SHELL_LEFT_FLOOR_PX : SHELL_AGENT_FLOOR_PX
    const decision = resolveShellColumnWidth({
      draggedPx: pointerPx,
      floorPx: floor,
      docked: false,
      lastExpandedPx: Math.max(pointerPx, floor),
      roomForFloor,
    })
    if (edge === 'left') {
      if (!decision.docked) {
        if (sidebarCollapsedRef.current) return
        collapsingLeftRef.current = true
        sidebarCollapsedRef.current = true
        setFlyoutOpen(false)
        setSidebarPinned(false)
        setSidebarCollapsed(true)
        return
      }
      const pct = dockedColumnPercent(decision.widthPx, false, agentCollapsedRef.current)
      if (pct == null) return
      lastExpandedLeftWidthRef.current = pct
      if (sidebarCollapsedRef.current) {
        sidebarCollapsedRef.current = false
        setExpandedLeftSize(pct)
        if (sidebarAutoHideRef.current) setSidebarPinned(true)
        setFlyoutOpen(false)
        setSidebarCollapsed(false)
        return
      }
      sidebarPanelRef.current?.resize(pct)
      return
    }
    if (!decision.docked) {
      if (agentCollapsedRef.current) return
      collapsingAgentRef.current = true
      agentCollapsedRef.current = true
      setAgentCollapsedPreservingLeftPx(true)
      return
    }
    const pct = dockedColumnPercent(decision.widthPx, sidebarCollapsedRef.current, false)
    if (pct == null) return
    lastExpandedChatWidthRef.current = pct
    if (agentCollapsedRef.current) {
      agentCollapsedRef.current = false
      setExpandedRightSize(pct)
      setAgentCollapsedPreservingLeftPx(false)
      return
    }
    chatPanelRef.current?.resize(pct)
  }

  gestureMoveRef.current = (ev: PointerEvent) => {
    if ((ev.buttons & 1) === 0) {
      gestureStopRef.current?.()
      return
    }
    const row = shellRowRef.current
    if (!row) return
    const rect = row.getBoundingClientRect()
    const room = measureShellRef.current()
    if (gestureEdgesRef.current.has('left')) {
      applySnapEdge('left', ev.clientX - rect.left, room.roomForLeft)
    }
    if (gestureEdgesRef.current.has('agent')) {
      applySnapEdge('agent', rect.right - ev.clientX, room.roomForAgent)
    }
  }

  gestureCommitRef.current = (edges) => {
    if (edges.has('left') && !sidebarCollapsedRef.current) {
      const pct = lastExpandedLeftWidthRef.current
      setExpandedLeftSize(pct)
      setPanelSizes((prev) => {
        const right = prev[2] ?? expandedRightSize
        return [pct, Math.max(0, 100 - pct - right), right]
      })
    }
    if (edges.has('agent') && !agentCollapsedRef.current) {
      const pct = lastExpandedChatWidthRef.current
      setExpandedRightSize(pct)
      setPanelSizes((prev) => {
        const left = prev[0] ?? expandedLeftSize
        return [left, Math.max(0, 100 - left - pct), pct]
      })
    }
  }

  const armSnapGesture = (edge: 'left' | 'agent') => {
    if (!shellDragRef.current) return
    gestureEdgesRef.current.add(edge)
    if (gestureStopRef.current) return
    snapGestureRef.current = true
    const armedAt = performance.now()
    let heldAfterArm = false
    const finish = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', stop)
      window.removeEventListener('pointercancel', stop)
      gestureStopRef.current = null
      snapGestureRef.current = false
      shellDragRef.current = false
      const edges = new Set(gestureEdgesRef.current)
      gestureEdgesRef.current.clear()
      gestureCommitRef.current(edges)
    }
    const move = (ev: PointerEvent) => {
      if (ev.buttons & 1) heldAfterArm = true
      gestureMoveRef.current(ev)
    }
    const stop = () => {
      // Unmounting the resize handle can emit a pointerup in the same turn.
      if (performance.now() - armedAt < 32) {
        window.setTimeout(() => {
          if (gestureStopRef.current !== stop || heldAfterArm) return
          finish()
        }, 40)
        return
      }
      finish()
    }
    gestureStopRef.current = stop
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', stop)
    window.addEventListener('pointercancel', stop)
  }

  const onShellDragPointerDown = useCallback(() => {
    shellDragRef.current = true
    const end = () => {
      window.removeEventListener('pointerup', end)
      window.removeEventListener('pointercancel', end)
      if (snapGestureRef.current) return
      shellDragRef.current = false
    }
    window.addEventListener('pointerup', end)
    window.addEventListener('pointercancel', end)
  }, [])

  /** Persist panel percents, and snap a column to the rail when it is under its floor. */
  const onShellPanelLayout = useCallback((sizes: number[]) => {
    if (snapGestureRef.current) return
    if (restoringChatWidthRef.current || restoringSidebarWidthRef.current) return
    if (collapsingLeftRef.current || collapsingAgentRef.current) return

    const row = shellRowRef.current?.clientWidth ?? 0
    const sidebarRailPx =
      showSidebar && sidebarCollapsed && !isFullscreen ? SHELL_ICON_RAIL_PX : 0
    const agentRailPx =
      showAgent && agentCollapsed && !isFullscreen ? SHELL_ICON_RAIL_PX : 0
    const group = shellPanelGroupWidthPx(row, { sidebarRailPx, agentRailPx })
    const pxOf = (pct: number | undefined) => columnPx(pct, group)

    const collapseLeft = () => {
      collapsingLeftRef.current = true
      sidebarCollapsedRef.current = true
      setFlyoutOpen(false)
      setSidebarPinned(false)
      setSidebarCollapsed(true)
      armSnapGesture('left')
    }
    const collapseAgent = () => {
      collapsingAgentRef.current = true
      agentCollapsedRef.current = true
      setAgentCollapsedPreservingLeftPx(true)
      armSnapGesture('agent')
    }
    const leftUnderFloor = (pct: number | undefined) => {
      const px = pxOf(pct)
      if (!(row > 0 && px > 0)) return false
      return !resolveShellColumnWidth({
        draggedPx: px,
        floorPx: SHELL_LEFT_FLOOR_PX,
        docked: true,
        lastExpandedPx: px,
        roomForFloor: true,
      }).docked
    }
    const agentUnderFloor = (pct: number | undefined) => {
      const px = pxOf(pct)
      if (!(row > 0 && px > 0)) return false
      return !resolveShellColumnWidth({
        draggedPx: px,
        floorPx: SHELL_AGENT_FLOOR_PX,
        docked: true,
        lastExpandedPx: px,
        roomForFloor: true,
      }).docked
    }

    if (sidebarInGroup && agentInGroup && sizes.length === 3) {
      const leftPct = sizes[0]
      const chatPct = sizes[2]
      const snapLeft = leftUnderFloor(leftPct)
      const snapAgent = agentUnderFloor(chatPct)
      if (snapLeft) collapseLeft()
      else if (leftPct !== undefined) {
        lastExpandedLeftWidthRef.current = leftPct
        setExpandedLeftSize(leftPct)
      }
      if (snapAgent) collapseAgent()
      else if (chatPct !== undefined) {
        lastExpandedChatWidthRef.current = chatPct
        setExpandedRightSize(chatPct)
      }
      if (!snapLeft && !snapAgent) setPanelSizes(sizes)
      return
    }

    if (sidebarInGroup && !agentInGroup && sizes.length === 2 && sizes[0] !== undefined) {
      if (leftUnderFloor(sizes[0])) {
        collapseLeft()
        return
      }
      lastExpandedLeftWidthRef.current = sizes[0]
      setExpandedLeftSize(sizes[0])
      setPanelSizes([
        sizes[0],
        sizes[1],
        expandedChatPanelPercent(panelSizes, expandedRightSize),
      ])
      return
    }

    if (!sidebarInGroup && agentInGroup && sizes.length === 2 && sizes[1] !== undefined) {
      if (agentUnderFloor(sizes[1])) {
        collapseAgent()
        return
      }
      lastExpandedChatWidthRef.current = sizes[1]
      setExpandedRightSize(sizes[1])
      setPanelSizes([
        panelSizes[0] || expandedLeftSize || 20,
        sizes[0],
        sizes[1],
      ])
    }
  }, [
    showSidebar,
    sidebarCollapsed,
    isFullscreen,
    showAgent,
    agentCollapsed,
    sidebarInGroup,
    agentInGroup,
    panelSizes,
    expandedLeftSize,
    expandedRightSize,
    setSidebarCollapsed,
    setAgentCollapsedPreservingLeftPx,
    setExpandedLeftSize,
    setExpandedRightSize,
    setPanelSizes,
  ])

  return {
    chatPanelRef,
    sidebarPanelRef,
    shellRowRef,
    restoringChatWidthRef,
    restoringSidebarWidthRef,
    lastExpandedChatWidthRef,
    lastExpandedLeftWidthRef,
    toggleSidebarCollapsed,
    persistChatExpandedWidthIfExpanded,
    setAgentCollapsedPreservingLeftPx,
    expandAgentPanel,
    expandLeftSidebar,
    toggleAgentCollapsed,
    toggleSidebarAutoHide,
    dockLeftFromRail,
    dockAgentFromRail,
    onShellPanelLayout,
    onShellDragPointerDown,
    flyoutOpen,
    setFlyoutOpen,
    sidebarAutoHide,
    leftDockDisabledReason,
    agentDockDisabledReason,
    roomForLeft: shellRoom.roomForLeft,
    roomForAgent: shellRoom.roomForAgent,
  }
}
